/**
 * Otto API client — REST + SSE.
 *
 * Routes through `env.STREAMING_API_URL` so chat streams hit the public ALB
 * rather than API Gateway (which has a 30s idle timeout that cuts off long
 * Otto turns). Mirrors `ScribeApiClient`.
 */

import {env} from '@/matic/config/env';
import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import {
  toOttoHistoryResult,
  toOttoSession,
  toOttoSessionSummary,
  toOttoSharedChat,
  toOttoShareResult,
  toOttoStreamEvent,
  toWireMessageRequest,
  toWireSessionContextUpdate,
  type SessionContextUpdate,
  type WireHistoryResponse,
  type WireSession,
  type WireSessionSummary,
  type WireSharedChat,
  type WireShareResponse,
} from './converters';
import type {
  OttoHistoryResult,
  OttoMessageRequest,
  OttoRunStatus,
  OttoSession,
  OttoSessionId,
  OttoSessionSummary,
  OttoSharedChat,
  OttoShareResult,
  OttoStreamCallbacks,
  PageType,
} from '../types/otto-models';

const BASE_PATH = '/api/v1/agents/otto';

// Safety net so the chat spinner always resolves. The server emits an SSE
// heartbeat comment every ~15s while a turn is alive (even mid-exploration with
// nothing to stream), so genuine silence this long means the stream died —
// dropped terminal frame, task recycle/OOM, or a proxy holding the socket open.
// Comfortably larger than the heartbeat interval to tolerate brief jitter.
const STREAM_STALL_TIMEOUT_MS = 45_000;

// Bounds the pre-response phase of a chat turn (session-create + request
// routing, before the first SSE byte arrives) — the stall watchdog above only
// arms once `response.body` is available, so a hang before headers land would
// otherwise wait forever with no signal to the UI. Generous vs. the
// ~0.5-2.3s create-session latencies seen in normal telemetry.
const CONNECT_TIMEOUT_MS = 30_000;

/** FastAPI's string `detail`, when the error body carries one. */
async function readDetail(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as {detail?: unknown};
    return typeof body.detail === 'string' && body.detail.trim()
      ? body.detail.trim()
      : undefined;
  } catch {
    return undefined;
  }
}

export class OttoApiClient extends AuthenticatedApiClient {
  constructor(token?: string) {
    super(token, env.STREAMING_API_URL);
  }

  /**
   * Resolve the most-recent active session for `(pageType, entityId)`, or
   * create one. Sessions are inert: navigating between entities never
   * mutates session state. When `forceNew` is true, always creates a fresh
   * active session (used by the explicit "New conversation" UI action).
   *
   * When `context` is provided, the new session is **born scoped** — the
   * backend sets the anchor/tagged refs on the freshly created row in the
   * same POST, collapsing the old create-then-PATCH round-trip. The context
   * is applied only when a new session is actually created (i.e. with
   * `forceNew`, or when no active session exists); it is ignored when an
   * existing active session is returned.
   */
  async createOrGetSession(
    pageType: PageType,
    entityId?: string,
    entityDisplayName?: string,
    forceNew = false,
    context?: SessionContextUpdate
  ): Promise<OttoSession> {
    const params = new URLSearchParams();
    // Wire format is snake_case — backend query params live outside the
    // converters layer so we set them directly.
    params.set('page_type', pageType);
    if (entityId) params.set('entity_id', entityId);
    if (entityDisplayName) params.set('entity_display_name', entityDisplayName);
    if (forceNew) params.set('force_new', 'true');
    const body = context ? toWireSessionContextUpdate(context) : {};
    const wire = await this.post<WireSession>(
      `${BASE_PATH}/sessions?${params.toString()}`,
      body
    );
    return toOttoSession(wire);
  }

  async getSession(sessionId: OttoSessionId): Promise<OttoSession> {
    const wire = await this.get<WireSession>(
      `${BASE_PATH}/sessions/${sessionId}`
    );
    return toOttoSession(wire);
  }

  /**
   * List the user's non-deleted sessions for a page type — both ACTIVE
   * and ARCHIVED rows are returned. Empty (never-messaged) sessions are
   * filtered server-side.
   */
  async listSessions(pageType: PageType): Promise<OttoSessionSummary[]> {
    const params = new URLSearchParams();
    params.set('page_type', pageType);
    const wire = await this.get<WireSessionSummary[]>(
      `${BASE_PATH}/sessions?${params.toString()}`
    );
    return wire.map((row) => toOttoSessionSummary(row));
  }

  /**
   * Update the chat-session anchor + tagged refs. Sends an explicit
   * `clear_anchor=true` to remove a previously-set anchor (passing
   * `anchor: null` alone is ambiguous on the wire).
   */
  async updateSessionContext(
    sessionId: OttoSessionId,
    update: SessionContextUpdate
  ): Promise<OttoSession> {
    const wire = await this.patch<WireSession>(
      `${BASE_PATH}/sessions/${sessionId}/context`,
      toWireSessionContextUpdate(update)
    );
    return toOttoSession(wire);
  }

  async archiveSession(sessionId: OttoSessionId): Promise<OttoSession> {
    const wire = await this.patch<WireSession>(
      `${BASE_PATH}/sessions/${sessionId}`,
      {status: 'archived'}
    );
    return toOttoSession(wire);
  }

  async restoreSession(sessionId: OttoSessionId): Promise<OttoSession> {
    const wire = await this.post<WireSession>(
      `${BASE_PATH}/sessions/${sessionId}/restore`,
      {}
    );
    return toOttoSession(wire);
  }

  /** Permanently delete a session and its on-disk transcript. Returns 204. */
  async hardDeleteSession(sessionId: OttoSessionId): Promise<void> {
    await this.delete<unknown>(`${BASE_PATH}/sessions/${sessionId}`);
  }

  /** Pin ("save") a chat — exempts it from the inactivity GC sweep. */
  async pinChat(sessionId: OttoSessionId): Promise<OttoSession> {
    const wire = await this.post<WireSession>(
      `${BASE_PATH}/sessions/${sessionId}/pin`,
      {}
    );
    return toOttoSession(wire);
  }

  /** Unpin a chat (re-enables the inactivity GC sweep). */
  async unpinChat(sessionId: OttoSessionId): Promise<OttoSession> {
    const wire = await this.post<WireSession>(
      `${BASE_PATH}/sessions/${sessionId}/unpin`,
      {}
    );
    return toOttoSession(wire);
  }

  /**
   * Freeze the chat into a tenant-readable snapshot and return its stable
   * deep link. Re-sharing re-freezes in place (same link).
   */
  async shareChat(sessionId: OttoSessionId): Promise<OttoShareResult> {
    const wire = await this.post<WireShareResponse>(
      `${BASE_PATH}/sessions/${sessionId}/share`,
      {}
    );
    return toOttoShareResult(wire);
  }

  /** Read a frozen shared chat snapshot (any authenticated tenant user). */
  async getSharedChat(shareId: string): Promise<OttoSharedChat> {
    const wire = await this.get<WireSharedChat>(
      `${BASE_PATH}/shared/${shareId}`
    );
    return toOttoSharedChat(wire);
  }

  /**
   * Read full session history. Strands `SlidingWindowConversationManager`
   * keeps the on-disk transcript bounded, so no pagination.
   */
  async getMessages(sessionId: OttoSessionId): Promise<OttoHistoryResult> {
    const wire = await this.get<WireHistoryResponse>(
      `${BASE_PATH}/sessions/${sessionId}/messages`
    );
    return toOttoHistoryResult(wire);
  }

  /**
   * Whether a background Otto run is in flight for this session. Polled by a
   * returning client to know when the detached turn's reply has landed.
   */
  async getRunStatus(sessionId: OttoSessionId): Promise<OttoRunStatus> {
    return this.get<OttoRunStatus>(
      `${BASE_PATH}/sessions/${sessionId}/run-status`
    );
  }

  /**
   * Session ids whose background run is currently in flight, for one page type.
   * Polled by the conversations list to show a per-row activity spinner. Single
   * read covers all of the user's sessions (in-memory registry, 1 pinned task).
   */
  async listActiveRunIds(pageType: PageType): Promise<OttoSessionId[]> {
    const params = new URLSearchParams();
    params.set('page_type', pageType);
    const wire = await this.get<{
      // eslint-disable-next-line @typescript-eslint/naming-convention -- snake_case wire shape
      active_session_ids: OttoSessionId[];
    }>(`${BASE_PATH}/active-run-ids?${params.toString()}`);
    return wire.active_session_ids;
  }

  /**
   * Cancel the in-flight background run for this session (Stop button). Targets
   * the run by id, so it works from any client — including a reattached one.
   */
  async cancelRun(sessionId: OttoSessionId): Promise<void> {
    await this.post<unknown>(
      `${BASE_PATH}/sessions/${sessionId}/run/cancel`,
      {}
    );
  }

  /**
   * Open the SSE chat stream. Returns an `abort` function that cancels the
   * underlying `fetch` and prevents further callbacks from firing.
   */
  streamMessage(
    sessionId: OttoSessionId,
    request: OttoMessageRequest,
    callbacks: OttoStreamCallbacks
  ): () => void {
    const controller = new AbortController();
    void this.runStream(sessionId, request, callbacks, controller);
    return () => {
      controller.abort();
    };
  }

  private async runStream(
    sessionId: OttoSessionId,
    request: OttoMessageRequest,
    callbacks: OttoStreamCallbacks,
    controller: AbortController
  ): Promise<void> {
    const url = `${this.baseUrl}${BASE_PATH}/sessions/${sessionId}/messages`;
    const headers = await this.prepareHeaders({});
    headers.set('Accept', 'text/event-stream');

    let response: Response;
    let timedOut = false;
    const connectTimer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, CONNECT_TIMEOUT_MS);
    try {
      // Self-heals an expired token: refreshes via signinSilent() and retries
      // once, rather than dead-ending on "Session expired" the way a raw fetch
      // would (see BaseApiClient.fetchWithAuthRetry).
      response = await this.fetchWithAuthRetry(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(toWireMessageRequest(request)),
        signal: controller.signal,
      });
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        if (timedOut) {
          callbacks.onError("Otto didn't respond in time. Please try again.");
        }

        return;
      }

      callbacks.onError('Connection lost. Try sending your message again.');
      return;
    } finally {
      clearTimeout(connectTimer);
    }

    if (!response.ok) {
      await this.handleHttpError(
        response,
        callbacks.onError,
        Boolean(request.images?.length)
      );
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError('Connection lost. Try sending your message again.');
      return;
    }

    await this.readSseStream(reader, callbacks);
  }

  private async handleHttpError(
    response: Response,
    onError: OttoStreamCallbacks['onError'],
    hadImages: boolean
  ): Promise<void> {
    if (hadImages && (response.status === 413 || response.status === 422)) {
      onError('Those images are too large to send. Try fewer or smaller ones.');
      return;
    }

    let message: string;
    switch (response.status) {
      case 401: {
        message = 'Session expired. Please refresh the page.';
        break;
      }

      case 403: {
        message = "You don't have permission to use Otto here.";
        break;
      }

      case 404: {
        message = 'Otto session not found. Please reopen Otto.';
        break;
      }

      case 409: {
        // 409 covers several conflicts (a run already in flight, an inactive session,
        // an archived anchor); the backend's detail says which.
        message =
          (await readDetail(response)) ??
          'This Otto session is no longer active.';
        break;
      }

      default: {
        message = 'Otto encountered an error. Please try again.';
      }
    }

    onError(message);
  }

  private async readSseStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    callbacks: OttoStreamCallbacks
  ): Promise<void> {
    const decoder = new TextDecoder();
    let buffer = '';
    let receivedTerminal = false;
    let stalled = false;

    const wrapped: OttoStreamCallbacks = {
      onEvent(event) {
        if (event.type === 'done' || event.type === 'error') {
          receivedTerminal = true;
        }

        callbacks.onEvent(event);
      },
      onError(message) {
        receivedTerminal = true;
        callbacks.onError(message);
      },
    };

    // Stall watchdog: reset on every received chunk (heartbeat OR data). When it
    // fires we cancel the reader, which resolves the pending read as done so the
    // loop exits cleanly and the stall branch below surfaces an error — the
    // spinner can never hang indefinitely.
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    const armWatchdog = () => {
      if (watchdog) clearTimeout(watchdog);
      watchdog = setTimeout(async () => {
        stalled = true;
        try {
          await reader.cancel();
        } catch {
          // Already errored/closed — nothing to release.
        }
      }, STREAM_STALL_TIMEOUT_MS);
    };

    try {
      armWatchdog();
      // SSE chunks must be read sequentially — each frame depends on the prior buffer.
      for (
        let chunk = await reader.read();
        !chunk.done;
        // eslint-disable-next-line no-await-in-loop
        chunk = await reader.read()
      ) {
        armWatchdog();
        buffer += decoder.decode(chunk.value, {stream: true});
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          if (frame.trim()) this.dispatchSseFrame(frame, wrapped);
        }
      }

      if (buffer.trim()) this.dispatchSseFrame(buffer, wrapped);

      if (!receivedTerminal) {
        callbacks.onError(
          stalled
            ? 'Otto stopped responding. Please try again.'
            : 'Response interrupted. Try sending your message again.'
        );
      }
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      callbacks.onError('Connection lost. Try sending your message again.');
    } finally {
      if (watchdog) clearTimeout(watchdog);
      // Release the underlying ReadableStream lock on abort/error/normal exit
      // so long-running sessions don't leak orphaned readers. cancel() may
      // reject if the stream is already errored; that's fine to ignore here.
      try {
        await reader.cancel();
      } catch {
        // ignore
      }
    }
  }

  private dispatchSseFrame(raw: string, callbacks: OttoStreamCallbacks): void {
    let eventType = '';
    const dataLines: string[] = [];

    // SSE allows multiple `data:` lines per event — concat with `\n`.
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) {
        eventType = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trim());
      }
    }

    if (!eventType || dataLines.length === 0) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(dataLines.join('\n'));
    } catch {
      return; // Malformed event — skip
    }

    const event = toOttoStreamEvent({type: eventType, data: parsed});
    if (event) callbacks.onEvent(event);
  }
}

// Singleton — `AuthenticatedApiClient` resolves the token dynamically per
// request via `prepareHeaders`, so no per-token cache is needed.
let cachedClient: OttoApiClient | undefined;

export function getOttoApi(): OttoApiClient {
  cachedClient ??= new OttoApiClient();
  return cachedClient;
}
